// ============================================================================
// File: frontend/src/components/dashboard/AIInsightsWidget.js
// Version: 130.0
// Date: 2025-12-17 01:20:00 EST
// Author: Dashboard Team
// Description: AI Insights widget with SMART NAVIGATION.
//              "Execute Fix" button passes state to /reputation to open the specific issue.
//
// Changelog:
// v130.0 - 🧠 SMART NAVIGATION: Added useNavigate with state (action: 'open_fix', targetId)
// v129.0 - Increased height and refined sorting
// ============================================================================

import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Activity, AlertTriangle, ArrowRight, Clock } from 'lucide-react';

// ── ПАЛИТРА ЭКРАНА «СЕГОДНЯ» → §3.1 (Ф1, quick-260909-jl8) ─────────────────
// Откат в каждой строке — ПРЕЖНИЙ литерал ЭТОГО ЖЕ места. На fitenko токен
// объявлен (styles/fitenko-tokens.css, селектор html.fitenko) и берётся он;
// на MLG и ELN токена НЕТ — берётся откат, то есть РОВНО прежний цвет.
// 🔴 Значения отката менять нельзя: ими доказывается неизменность чужих арендаторов.
// 🔴 Голый var(--ink) запрещён: у MLG он разрешается в ПУСТОТУ (отсутствие цвета),
//    а не в другой цвет — объявление становится недействительным.
// Класс C (подложки чипов) — по решению владельца от 2026-09-09, вариант B:
// подложка выводится правилом color-mix(in srgb, АКЦЕНТ 14%, --surface); правило
// объявлено ОДИН раз в pages/Dashboard.css под селектором html.fitenko. Бирюза —
// вариант T1: своего тона в §3.1 нет, берётся --green-accent.
const P = {
    canvas:      'var(--canvas, #f8fafc)',  // класс A
    canvasAlt:   'var(--canvas, #fafafa)',  // класс A
    ink:         'var(--ink, #1e293b)',  // класс A
    inkDeep:     'var(--ink, #0f172a)',  // класс A
    line:        'var(--line, #e2e8f0)',  // класс A
    lineStrong:  'var(--line, #cbd5e1)',  // класс A
    muted:       'var(--ink-muted, #64748b)',  // класс A
    mutedSoft:   'var(--ink-muted, #94a3b8)',  // класс A
    surfaceSoft: 'var(--surface-soft, #f1f5f9)',  // класс A
    blue1:       'var(--blue-accent, #3b82f6)',  // класс B
    red2:        'var(--red-accent, #ef4444)',  // класс B
    chipBlue2:   'var(--chip-blue-bg, #dbeafe)',  // класс C
    chipRed1:    'var(--chip-red-bg, #fee2e2)',  // класс C
    chipRed2:    'var(--chip-red-bg, #fecaca)',  // класс C
};




const AIInsightsWidget = ({ events }) => {
    const navigate = useNavigate();

    // === [НОВОЕ] Умная навигация к конкретной проблеме ===
    const handleExecuteFix = (item) => {
        console.log("Navigating to fix:", item.id, item.type);
        navigate('/reputation', { 
            state: { 
                action: 'open_fix', 
                targetId: item.id,
                type: item.type 
            } 
        });
    };
    // =====================================================

    const items = events || [];
    const limitedItems = items.slice(0, 25);

    // Sort: CRITICAL first
    const sortedItems = [...limitedItems].sort((a, b) => {
        const aCritical = a.type && (
            a.type.toLowerCase().includes('critical') || 
            a.type.toLowerCase().includes('alert')
        );
        const bCritical = b.type && (
            b.type.toLowerCase().includes('critical') || 
            b.type.toLowerCase().includes('alert')
        );
        if (aCritical && !bCritical) return -1;
        if (!aCritical && bCritical) return 1;
        return 0;
    });

    if (sortedItems.length === 0) {
        return null;
    }

    return (
        <div className="card" style={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            background: 'white', 
            borderRadius: '16px', 
            overflow: 'hidden',
            border: `1px solid ${P.surfaceSoft}`, 
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            minHeight: '500px',
            maxHeight: '900px'
        }}>
            {/* HEADER */}
            <div style={{ 
                padding: '20px 24px 16px 24px', 
                borderBottom: `1px solid ${P.canvas}`, 
                flexShrink: 0,
                background: 'white'
            }}>
                <h3 style={{ 
                    fontSize: '1.1rem', 
                    fontWeight: '700', 
                    color: P.ink, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    margin: 0,
                    marginBottom: '8px'
                }}>
                    <Activity size={20} style={{ stroke: P.blue1 }} /> Recent Activity
                </h3>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px'
                }}>
                    <div style={{width:'14px', textAlign:'center', fontSize: '14px'}}>✨</div>
                    <span style={{ 
                        fontSize: '0.7rem', 
                        fontWeight: '800', 
                        color: P.mutedSoft, 
                        textTransform: 'uppercase', 
                        letterSpacing: '0.5px'
                    }}>
                        AI INSIGHTS
                    </span>
                </div>
            </div>

            {/* SCROLLABLE CONTENT */}
            <div style={{ 
                flex: 1,
                overflowY: 'auto',
                padding: '16px 24px',
                minHeight: 0,
                background: P.canvasAlt
            }} className="ai-scroll">
                {sortedItems.map((item, idx) => {
                    const isCritical = item.type && (
                        item.type.toLowerCase().includes('critical') || 
                        item.type.toLowerCase().includes('alert')
                    );
                    
                    return (
                        <div key={item.id || idx} style={{ 
                            background: 'white', 
                            border: `2px solid ${isCritical ? P.chipRed2 : P.line}`, 
                            borderRadius: '12px',
                            padding: '14px',
                            marginBottom: '12px',
                            boxShadow: isCritical 
                                ? '0 4px 6px rgba(239, 68, 68, 0.1)' 
                                : '0 1px 3px rgba(0,0,0,0.02)',
                            transition: 'all 0.2s'
                        }}>
                            {/* Badge + Time */}
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                marginBottom: '10px'
                            }}>
                                <div style={{ 
                                    display: 'inline-flex', 
                                    alignItems: 'center', 
                                    gap: '6px',
                                    padding: '4px 10px',
                                    borderRadius: '8px',
                                    background: isCritical ? P.chipRed1 : P.chipBlue2,
                                    fontSize: '0.65rem', 
                                    fontWeight: '800', 
                                    color: isCritical ? P.red2 : P.blue1, 
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    {isCritical && <AlertTriangle size={12} />}
                                    {isCritical ? '🔴 CRITICAL ISSUE' : '💡 INSIGHT'}
                                </div>
                                <span style={{ 
                                    fontSize: '0.65rem',
                                    color: P.mutedSoft,
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '4px'
                                }}>
                                    <Clock size={11} />
                                    {item.time}
                                </span>
                            </div>
                            
                            {/* Title */}
                            <div style={{ 
                                fontWeight: '700', 
                                fontSize: '0.95rem',
                                color: P.ink, 
                                marginBottom: '8px',
                                lineHeight: '1.3'
                            }}>
                                {item.title}
                            </div>
                            
                            {/* Description */}
                            <div style={{ 
                                fontSize: '0.8rem',
                                color: P.muted, 
                                marginBottom: '12px',
                                lineHeight: '1.5'
                            }}>
                                {item.description}
                            </div>
                            
                            {/* Execute Fix Button (only for CRITICAL) */}
                            {isCritical && (
                                <button
                                    onClick={() => handleExecuteFix(item)}
                                    style={{
                                        width: '100%', 
                                        padding: '12px',
                                        borderRadius: '8px',
                                        background: P.ink, 
                                        color: 'white', 
                                        border: 'none',
                                        fontSize: '0.85rem',
                                        fontWeight: '700', 
                                        cursor: 'pointer',
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        gap: '8px',
                                        transition: 'background 0.2s',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = P.inkDeep}
                                    onMouseLeave={(e) => e.target.style.background = P.ink}
                                >
                                    Execute Fix <ArrowRight size={16} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
            
            {/* Custom scrollbar styles */}
            <style>{`
                .ai-scroll::-webkit-scrollbar {
                    width: 6px;
                }
                .ai-scroll::-webkit-scrollbar-track {
                    background: ${P.canvas};
                    border-radius: 10px;
                }
                .ai-scroll::-webkit-scrollbar-thumb {
                    background: ${P.lineStrong};
                    border-radius: 10px;
                }
                .ai-scroll::-webkit-scrollbar-thumb:hover {
                    background: ${P.mutedSoft};
                }
                .ai-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: ${P.lineStrong} ${P.canvas};
                }
            `}</style>
        </div>
    );
};

AIInsightsWidget.propTypes = {
    events: PropTypes.array
};

AIInsightsWidget.defaultProps = {
    events: []
};

export default AIInsightsWidget;